<script lang="ts">

    import Modal from "@/components/Modal.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type {Field} from "@/types/types";
    import {useForm} from "@/lib/precognition.svelte";
    import {toast} from "svelte-sonner";
    import type Employees from "@/schemas/public/Employees";
    import RelatedSingle from "@/components/Inputs/RelatedSingle.svelte";
    import JobTable from "@/components/jobs/JobTable.svelte";
    import VehicleTable from "@/components/vehicles/VehicleTable.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        employee : Employees;
    };

    let {blocks, show = $bindable(), employee }: Props = $props();
    let isSelectVehicalOpen = $state(false);
    let vehicleLabel: string | null | undefined = $state(null);


    let formInner: Record<string, any> = {};
    for (const block of blocks) {
        for (const field of block.fields) {
            formInner[field.column_name] = field.value;


            if (field.ui_type === 'related') {
                formInner[field.column_name + "_entity_label"] = field.value_label;
                vehicleLabel = field.value_label;
            }
        }
    }
    let form = $state(useForm(formInner, 'put', route('employees.update', { employee: employee.id })));

    function submit(e) {
        $form.processing = true;
        e.preventDefault();
        $form.put(route('employees.update', { employee: employee.id }), {
            onSuccess: () => {
                close();
                $form.reset();
                toast.success("Employee edited successfully!");
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error("Error editing employee. " + message as string);
                }
            }
        });
    }

    function close() {
        show = false;
    }

</script>

{#if show}
    <Modal title="Edit Employee" onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#each blocks as block(block.id)}
                    <FormBlock label={block.label}>
                        {#each block.fields as field(field.id)}
                            {#if field.editable}
                                {#if field.ui_type === 'related'}
                                    <RelatedSingle
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        bind:isOpen={isSelectVehicalOpen}
                                        bind:valLabel={vehicleLabel}
                                    >
                                        <VehicleTable
                                            view="related"
                                            onRowClick={(row) => {
                                                $form.job_id = row?.id ?? null;
                                                vehicleLabel = row?.registration ?? '';
                                                isSelectVehicalOpen = false;
                                            }}
                                        />
                                    </RelatedSingle>
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        options={parseFieldOptions(field)}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else}
                                    <Input
                                        field={field}
                                        bind:value={$form[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$form.processing}>
                        {#if $form.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}
